import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { PageHeader } from "@/components/layout/AppShell";
import { Card, StatCard } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { TenderListSkeleton } from "@/components/ui/Skeleton";
import { Pagination, PageInfo } from "@/components/ui/Pagination";
import {
  TenderFilters,
  type FilterValues,
} from "@/components/tenders/TenderFilters";
import {
  TenderCard,
  TenderRow,
  TenderEmptyState,
} from "@/components/tenders/TenderCard";
import {
  useTenderList,
  useTenderStats,
  type TenderFilters as TenderFilterOptions,
} from "@/hooks/useTenders";
import { cn, formatTHB } from "@/lib/utils";

type ViewMode = "cards" | "table";

export function TendersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<ViewMode>("cards");
  const [showFilters, setShowFilters] = useState(true);

  // Parse filters from URL
  const filters = useMemo<TenderFilterOptions>(
    () => ({
      search: searchParams.get("q") || undefined,
      status:
        searchParams.getAll("status").length > 0
          ? searchParams.getAll("status")
          : undefined,
      province: searchParams.get("province") || undefined,
      procurementMethod:
        searchParams.getAll("method").length > 0
          ? searchParams.getAll("method")
          : undefined,
      minBudget: searchParams.get("minBudget")
        ? parseInt(searchParams.get("minBudget")!) * 100
        : undefined,
      maxBudget: searchParams.get("maxBudget")
        ? parseInt(searchParams.get("maxBudget")!) * 100
        : undefined,
      dateFrom: searchParams.get("dateFrom") || undefined,
      dateTo: searchParams.get("dateTo") || undefined,
      page: parseInt(searchParams.get("page") || "1"),
      perPage: 20,
    }),
    [searchParams],
  );

  const { data, isLoading, error } = useTenderList(filters);
  const { data: stats } = useTenderStats(filters, { enabled: true });

  const handleFiltersChange = (newFilters: FilterValues) => {
    const params = new URLSearchParams();

    if (newFilters.search) params.set("q", newFilters.search);
    if (newFilters.province) params.set("province", newFilters.province);
    if (newFilters.minBudget) params.set("minBudget", newFilters.minBudget);
    if (newFilters.maxBudget) params.set("maxBudget", newFilters.maxBudget);
    if (newFilters.dateFrom) params.set("dateFrom", newFilters.dateFrom);
    if (newFilters.dateTo) params.set("dateTo", newFilters.dateTo);
    newFilters.status?.forEach((s) => params.append("status", s));
    newFilters.procurementMethod?.forEach((m) => params.append("method", m));

    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(page));
    setSearchParams(params);
  };

  return (
    <div>
      <PageHeader
        title="ประกาศจัดซื้อจัดจ้าง"
        description="เรียกดูและกรองประกาศจัดซื้อจัดจ้างภาครัฐ"
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center p-1 bg-slate-100 rounded-lg">
              <button
                onClick={() => setViewMode("cards")}
                className={cn(
                  "p-1.5 rounded-md transition-all",
                  viewMode === "cards"
                    ? "bg-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                <Icon
                  icon="solar:widget-2-line-duotone"
                  className="w-4 h-4"
                />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={cn(
                  "p-1.5 rounded-md transition-all",
                  viewMode === "table"
                    ? "bg-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                <Icon icon="solar:list-line-duotone" className="w-4 h-4" />
              </button>
            </div>

            {/* Filter toggle */}
            <Button
              variant={showFilters ? "secondary" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Icon icon="solar:filter-line-duotone" className="w-4 h-4 mr-2" />
              ตัวกรอง
            </Button>
          </div>
        }
      />

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard
            title="จำนวนประกาศ"
            value={stats.totalCount.toLocaleString()}
            icon={
              <Icon
                icon="solar:document-text-line-duotone"
                className="w-5 h-5"
              />
            }
          />
          <StatCard
            title="เปิดรับข้อเสนอ"
            value={stats.biddingCount.toLocaleString()}
            icon={
              <Icon icon="solar:hand-money-line-duotone" className="w-5 h-5" />
            }
          />
          <StatCard
            title="งบประมาณรวม"
            value={formatTHB(stats.totalBudget)}
            icon={<Icon icon="solar:wallet-line-duotone" className="w-5 h-5" />}
          />
        </div>
      )}

      {/* Filters at top */}
      {showFilters && (
        <div className="mb-6">
          <TenderFilters onFiltersChange={handleFiltersChange} />
        </div>
      )}

      {/* Main content */}
      <div>
        {/* Tender list */}
        <div className="flex-1 min-w-0">
          {/* Results info */}
          {data && (
            <div className="flex items-center justify-between mb-4">
              <PageInfo
                currentPage={data.page}
                pageSize={data.perPage}
                totalItems={data.totalItems}
              />

              <select className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="-published_at">ใหม่สุดก่อน</option>
                <option value="published_at">เก่าสุดก่อน</option>
                <option value="-sum_price_agree">งบประมาณสูงสุด</option>
                <option value="sum_price_agree">งบประมาณต่ำสุด</option>
              </select>
            </div>
          )}

          {/* Loading state */}
          {isLoading && <TenderListSkeleton count={5} />}

          {/* Error state */}
          {error && (
            <Card className="p-6 text-center">
              <Icon
                icon="solar:danger-circle-line-duotone"
                className="w-12 h-12 text-rose-400 mx-auto mb-3"
              />
              <h3 className="font-semibold text-slate-900">
                โหลดประกาศไม่สำเร็จ
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {error instanceof Error ? error.message : "เกิดข้อผิดพลาด"}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => window.location.reload()}
              >
                ลองอีกครั้ง
              </Button>
            </Card>
          )}

          {/* Empty state */}
          {data && data.items.length === 0 && <TenderEmptyState />}

          {/* Tender list */}
          {data && data.items.length > 0 && (
            <>
              {viewMode === "cards" ? (
                <div className="flex flex-col gap-3">
                  {data.items.map((tender) => (
                    <TenderCard key={tender.id} tender={tender} />
                  ))}
                </div>
              ) : (
                <Card padding="none">
                  {/* Table header */}
                  <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    <div className="col-span-5">โครงการ</div>
                    <div className="col-span-2">สถานะ</div>
                    <div className="col-span-2">งบประมาณ</div>
                    <div className="col-span-2">วันที่</div>
                    <div className="col-span-1"></div>
                  </div>
                  {/* Table rows */}
                  {data.items.map((tender) => (
                    <TenderRow key={tender.id} tender={tender} />
                  ))}
                </Card>
              )}

              {/* Pagination */}
              <div className="mt-6">
                <Pagination
                  currentPage={data.page}
                  totalPages={data.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
